/* touch-context-bridge.ts
 *
 * Global bridge that enables context menus on touch devices (iPad, tablets).
 * Instead of patching 50+ individual onContextMenu handlers, this module
 * adds a global touch listener that detects long-press and dispatches a
 * synthetic contextmenu event with the correct coordinates.
 *
 * This way, every existing onContextMenu handler in the app automatically
 * works on iPad with long-press — zero component changes needed.
 */
// @ts-nocheck

const LONG_PRESS_MS = 500;
const MOVE_THRESHOLD_PX = 10;

let installed = false;
let timer = null;
let startX = 0;
let startY = 0;
let targetEl = null;

function clear() {
  if (timer) { clearTimeout(timer); timer = null; }
  targetEl = null;
}

function onTouchStart(e) {
  // Ignore touches on open context menus
  if (e.target instanceof Element && e.target.closest('.nv-ctxmenu')) {
    clear();
    return;
  }
  // Only handle single-finger touches
  if (e.touches.length !== 1) { clear(); return; }

  const touch = e.touches[0];
  startX = touch.clientX;
  startY = touch.clientY;
  targetEl = e.currentTarget; // document

  timer = setTimeout(() => {
    if (!targetEl) return;
    // Find the element under the finger
    const el = document.elementFromPoint(startX, startY);
    if (!el) return;

    // Walk up the DOM to find the closest element with an onContextMenu React handler
    let candidate = el;
    while (candidate && candidate !== document.body) {
      // React attaches event handlers via __reactFiber or __reactEvents
      // Check if this element or its React handler has contextmenu handling
      const reactKey = Object.keys(candidate).find(k =>
        k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
      );
      if (reactKey) {
        // Check fiber for onContextMenu prop
        let fiber = candidate[reactKey];
        let hasCtxMenu = false;
        let depth = 0;
        while (fiber && depth < 5) {
          if (fiber.memoizedProps?.onContextMenu) {
            hasCtxMenu = true;
            break;
          }
          if (fiber.pendingProps?.onContextMenu) {
            hasCtxMenu = true;
            break;
          }
          fiber = fiber.return;
          depth++;
        }
        if (hasCtxMenu) break;
      }

      // Also check direct oncontextmenu attribute
      if (candidate.getAttribute('oncontextmenu') || candidate.oncontextmenu) break;

      // Check for nv-tree-row or cursor-context-menu CSS class (our signals)
      if (candidate.classList?.contains('nv-tree-row') ||
          candidate.classList?.contains('cursor-context-menu') ||
          candidate.closest?.('[class*="nv-tree-row"]')) break;

      candidate = candidate.parentElement;
    }

    // Vibrate if supported
    if (navigator.vibrate) navigator.vibrate(30);

    // Dispatch a synthetic contextmenu event at the touch point.
    // React's event delegation will pick it up and route to the onContextMenu handler.
    const syntheticEvent = new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: startX,
      clientY: startY,
      button: 2, // right button
    });
    // Prevent the native iOS long-press menu (callout/copy)
    document.addEventListener('contextmenu', function blockNative(ev) {
      ev.preventDefault();
      document.removeEventListener('contextmenu', blockNative, true);
    }, true);

    el.dispatchEvent(syntheticEvent);
  }, LONG_PRESS_MS);
}

function onTouchMove(e) {
  if (!timer) return;
  const touch = e.touches[0];
  if (!touch) { clear(); return; }
  const dx = Math.abs(touch.clientX - startX);
  const dy = Math.abs(touch.clientY - startY);
  if (dx > MOVE_THRESHOLD_PX || dy > MOVE_THRESHOLD_PX) {
    clear();
  }
}

function onTouchEnd() {
  clear();
}

/**
 * Install the global touch→contextmenu bridge.
 * Call once at app mount. Safe to call multiple times (idempotent).
 */
export function installTouchContextBridge() {
  if (installed) return;
  if (typeof window === 'undefined') return;
  // Only install on touch-capable devices
  if (!('ontouchstart' in window) && !navigator.maxTouchPoints) return;

  installed = true;
  document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
  document.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
  document.addEventListener('touchcancel', onTouchEnd, { passive: true });

  // Prevent native iOS callout / context menu on long-press
  document.addEventListener('contextmenu', (e) => {
    // Only prevent if it was touch-initiated (our synthetic or native touch long-press)
    if (e.sourceCapabilities?.firesTouchEvents || e.pointerType === 'touch') {
      e.preventDefault();
    }
  }, true);
}

/**
 * Remove the bridge (for testing / HMR).
 */
export function uninstallTouchContextBridge() {
  if (!installed) return;
  installed = false;
  document.removeEventListener('touchstart', onTouchStart, true);
  document.removeEventListener('touchmove', onTouchMove, true);
  document.removeEventListener('touchend', onTouchEnd);
  document.removeEventListener('touchcancel', onTouchEnd);
}
